import type { ILogisticsRevisionHandle } from '../apis/logisticsRevision.js';

export interface ILogisticsRevisionSnapshot {
  /** The revision the page's data stands at; null before the first load. */
  loadedRevision: number | null;
  /** A write came back `logistics-stale`. */
  staleRefused: boolean;
}

export interface ILogisticsRevisionStore extends ILogisticsRevisionHandle {
  /** "Load": the data now stands at `revision`. Never a
   *  background refetch — see the hook. */
  load(revision: number): void;
  /** Whether one of the page's own writes is waiting for its answer. Read by
   *  `isStale`; not part of the snapshot, so bracketing a write renders
   *  nothing. */
  isWriteInFlight(): boolean;
  /** The server's latest answer from the poll. Held only so that ending the
   *  last write can re-check staleness without a network call: an answer that
   *  landed mid-write was ignored by `isStale` and nothing else would render. */
  observe(serverRevision: number): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): ILogisticsRevisionSnapshot;
}

/** The page's revision as a tiny external store: written from mutation
 *  callbacks (outside render), read with `useSyncExternalStore`. The snapshot
 *  is replaced only when a field changes, so a no-op write never renders. */
export function createLogisticsRevisionStore(initialRevision: number | null = null): ILogisticsRevisionStore {
  let snapshot: ILogisticsRevisionSnapshot = { loadedRevision: initialRevision, staleRefused: false };
  let writesInFlight = 0;
  let observedRevision = 0;
  let queue: Promise<unknown> = Promise.resolve();
  const listeners = new Set<() => void>();
  const set = (next: ILogisticsRevisionSnapshot) => {
    if (next.loadedRevision === snapshot.loadedRevision && next.staleRefused === snapshot.staleRefused)
      return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    current: () => snapshot.loadedRevision ?? 0,
    adopt: (revision) =>
      set({ ...snapshot, loadedRevision: Math.max(snapshot.loadedRevision ?? 0, revision) }),
    load: (revision) => set({ loadedRevision: revision, staleRefused: false }),
    // `begin`/`end` bracket a write; `run` queues it behind the one before.
    begin: () => {
      writesInFlight += 1;
    },
    end: () => {
      writesInFlight -= 1;
      // A poll answer ahead of us was ignored while the write ran: a new
      // snapshot object (same fields) makes the page re-evaluate it now.
      if (writesInFlight === 0 && observedRevision > (snapshot.loadedRevision ?? 0)) {
        snapshot = { ...snapshot };
        listeners.forEach((listener) => listener());
      }
    },
    isWriteInFlight: () => writesInFlight > 0,
    observe: (serverRevision) => {
      observedRevision = Math.max(observedRevision, serverRevision);
    },
    markStale: () => set({ ...snapshot, staleRefused: true }),
    run: (task) => {
      const result = queue.then(task);
      // The next task waits for this one to settle, whichever way.
      queue = result.then(
        () => undefined,
        () => undefined,
      );
      return result;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
  };
}

/** Locked: a write was refused, or — with none of our own writes in flight —
 *  the server has moved past the revision our data stands at. */
export function isStale(
  snapshot: ILogisticsRevisionSnapshot,
  serverRevision: number | undefined,
  writeInFlight: boolean,
): boolean {
  if (snapshot.staleRefused) return true;
  if (snapshot.loadedRevision === null || serverRevision === undefined || writeInFlight) return false;
  return serverRevision > snapshot.loadedRevision;
}
